import React from 'react';
import styles from './index.module.scss';

import { If } from 'utils';
import { UiKit } from 'components';
import { useId } from 'utils/hooks';
import { useRadio } from '../context';

/**
 * A single radio option component
 *
 * @param {Object} props
 * @param {string} props.id - Unique identifier for the radio input
 * @param {string} props.value - The value associated with this radio option
 * @param {string} props.label - Label text or component to display next to radio
 * @param {string} props.tooltipText - Tooltip text for the label
 * @param {boolean} props.disabled - Whether the radio option is disabled
 * @param {React.ReactNode} props.children - Additional content to render
 * @param {import('utils/types').Weight} props.labelWeight - The font weight for the label
 *
 * @returns {JSX.Element}
 */
export default function RadioOption({
  id = '',
  value = '',
  label = '',
  disabled = false,
  tooltipText = '',
  children = null,
  labelWeight = 'normal'
}) {
  const {
    name,
    value: selectedValue,
    onChange,
    disabled: isRootDisabled
  } = useRadio();

  const fallbackId = useId('radio');

  const inputId = id || fallbackId;
  const isSelected = selectedValue === value;
  const isDisabled = isRootDisabled || disabled;

  const onSelect = event => {
    if (isSelected || isDisabled) return;
    onChange(value, event);
  };

  return (
    <li className={styles.container}>
      <input
        id={inputId}
        type="radio"
        name={name}
        checked={isSelected}
        onChange={onSelect}
        disabled={isDisabled}
        className={styles.input}
      />
      <UiKit.Label forceMount htmlFor={inputId} className={styles.marker}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
        >
          <circle cx="8" cy="8" r="7.5" fill="white" className={styles.ring} />
          <circle cx="8" cy="8" r="4" className={styles.dot} />
        </svg>
      </UiKit.Label>
      <If test={label}>
        <div className={styles.content}>
          <UiKit.Label
            htmlFor={inputId}
            text={label}
            weight={labelWeight}
            tooltipText={tooltipText}
            className={styles.label}
          />
          {children}
        </div>
      </If>
    </li>
  );
}
